import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Plus, 
  Eye, 
  MessageSquare, 
  CheckCircle, 
  Clock, 
  XCircle, 
  AlertTriangle,
  TrendingUp,
  DollarSign,
  FileText,
  Star,
  Award,
  Calendar,
  MapPin,
  Download,
  Upload,
  Send,
  RefreshCw,
  MoreHorizontal
} from 'lucide-react';

const ProjectVerificationDashboard = ({ projectOwnerId = 'PO001' }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProposalDetails, setShowProposalDetails] = useState(null);

  // Mock data for project owner's verification requests
  const [verificationRequests] = useState([
    {
      id: 'VR001',
      projectId: 'P001',
      projectName: 'Amazon Rainforest Restoration',
      status: 'proposals-received',
      requestDate: '2024-01-15',
      deadline: '2024-04-15',
      budgetRange: '50000-100000',
      preferredStandards: ['Gold Standard', 'VCS'],
      description: 'Seeking comprehensive verification for large-scale reforestation project with community benefits tracking.',
      proposalsCount: 5,
      estimatedCredits: 150000,
      projectType: 'Reforestation',
      location: 'Brazil',
      proposals: [
        {
          id: 'PR001',
          verifierId: 'V001',
          verifierName: 'EcoVerify Solutions',
          verifierRating: 4.9,
          verifierExperience: '8 years',
          completedProjects: 45,
          price: 75000,
          timeline: '4 months',
          methodology: 'Gold Standard CDM methodology with satellite monitoring',
          scope: 'Full project verification including baseline, monitoring, and community impact assessment',
          submittedDate: '2024-01-18',
          credentials: ['Gold Standard', 'VCS', 'CDM'],
          portfolio: [
            { name: 'Tropical Forest Conservation Brazil', credits: 120000, year: 2023 },
            { name: 'Mangrove Restoration Indonesia', credits: 85000, year: 2023 }
          ]
        },
        {
          id: 'PR002',
          verifierId: 'V002',
          verifierName: 'Carbon Trust Verifiers',
          verifierRating: 4.7,
          verifierExperience: '10 years',
          completedProjects: 78,
          price: 68000,
          timeline: '5 months',
          methodology: 'VCS methodology with ground-based monitoring and drone verification',
          scope: 'Complete verification package with quarterly monitoring reports',
          submittedDate: '2024-01-19',
          credentials: ['VCS', 'CAR', 'ACR'],
          portfolio: [
            { name: 'Amazon Basin REDD+ Project', credits: 200000, year: 2023 },
            { name: 'Atlantic Forest Restoration', credits: 95000, year: 2022 }
          ]
        }
      ]
    },
    {
      id: 'VR002',
      projectId: 'P002',
      projectName: 'Solar Farm Efficiency Project',
      status: 'in-progress',
      requestDate: '2024-01-10',
      deadline: '2024-03-10',
      budgetRange: '25000-50000',
      preferredStandards: ['CAR', 'ACR'],
      description: 'Verification needed for solar farm efficiency improvements and grid integration benefits.',
      proposalsCount: 3,
      estimatedCredits: 80000,
      projectType: 'Renewable Energy',
      location: 'California, USA',
      acceptedProposal: {
        id: 'PR003',
        verifierId: 'V003',
        verifierName: 'Renewable Verification Corp',
        price: 42000,
        timeline: '3 months',
        startDate: '2024-01-25'
      },
      progress: 45,
      milestones: [
        { id: 1, name: 'Project Kick-off', status: 'completed', date: '2024-01-25', description: 'Initial project setup and documentation review' },
        { id: 2, name: 'Site Assessment', status: 'completed', date: '2024-02-05', description: 'On-site inspection and data collection' },
        { id: 3, name: 'Data Analysis', status: 'in-progress', date: '2024-02-20', description: 'Analysis of collected data and preliminary findings' },
        { id: 4, name: 'Draft Report', status: 'pending', date: '2024-03-01', description: 'Preparation of draft verification report' },
        { id: 5, name: 'Final Verification', status: 'pending', date: '2024-03-10', description: 'Final report and verification completion' }
      ],
      communications: [
        { date: '2024-02-10', from: 'Verifier', type: 'update', content: 'Site assessment completed. Preliminary data looks promising.' },
        { date: '2024-02-05', from: 'Project Owner', type: 'message', content: 'Additional baseline data uploaded to document portal.' },
        { date: '2024-01-30', from: 'Verifier', type: 'report', content: 'Initial assessment report submitted for review.' }
      ]
    }
  ]);

  const [projects] = useState([
    {
      id: 'P001',
      name: 'Amazon Rainforest Restoration',
      type: 'Reforestation',
      location: 'Brazil',
      estimatedCredits: 150000,
      status: 'approved',
      verificationStatus: 'unverified',
      credibilityScore: 50
    },
    {
      id: 'P002',
      name: 'Solar Farm Efficiency Project',
      type: 'Renewable Energy',
      location: 'California, USA',
      estimatedCredits: 80000,
      status: 'approved',
      verificationStatus: 'under-verification',
      credibilityScore: 65
    },
    {
      id: 'P003',
      name: 'Wind Energy Development',
      type: 'Renewable Energy',
      location: 'Texas, USA',
      estimatedCredits: 120000,
      status: 'approved',
      verificationStatus: 'verified',
      credibilityScore: 85
    }
  ]);

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'proposals-received': 'bg-blue-100 text-blue-800',
      'proposal-accepted': 'bg-green-100 text-green-800',
      'in-progress': 'bg-purple-100 text-purple-800',
      'completed': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'unverified': 'bg-gray-100 text-gray-800',
      'under-verification': 'bg-blue-100 text-blue-800',
      'verified': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Awaiting Proposals',
      'proposals-received': 'Proposals Received',
      'proposal-accepted': 'Proposal Accepted',
      'in-progress': 'Verification In Progress',
      'completed': 'Completed',
      'rejected': 'Rejected',
      'unverified': 'Unverified',
      'under-verification': 'Under Verification',
      'verified': 'Verified'
    };
    return labels[status] || status;
  };

  const handleAcceptProposal = async (requestId, proposalId) => {
    try {
      console.log('Accepting proposal:', { requestId, proposalId });
      alert('Proposal accepted! Verification process will begin.');
    } catch (error) {
      console.error('Error accepting proposal:', error);
      alert('Error accepting proposal. Please try again.');
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Requests</p>
              <p className="text-2xl font-semibold text-gray-900">
                {verificationRequests.filter(r => ['pending', 'proposals-received', 'in-progress'].includes(r.status)).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Verified Projects</p>
              <p className="text-2xl font-semibold text-gray-900">
                {projects.filter(p => p.verificationStatus === 'verified').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Budget</p>
              <p className="text-2xl font-semibold text-gray-900">$485k</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <TrendingUp className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg. Credibility</p>
              <p className="text-2xl font-semibold text-gray-900">
                {Math.round(projects.reduce((sum, p) => sum + p.credibilityScore, 0) / projects.length)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center space-x-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
          >
            <Plus className="h-5 w-5 text-gray-500" />
            <span className="text-gray-700 font-medium">Create Verification Request</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Search className="h-5 w-5 text-gray-500" />
            <span className="text-gray-700 font-medium">Browse Verifiers</span>
          </button>
          
          <button className="flex items-center justify-center space-x-2 p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Award className="h-5 w-5 text-gray-500" />
            <span className="text-gray-700 font-medium">View Certifications</span>
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Requests</h3>
          <div className="space-y-4">
            {verificationRequests.slice(0, 3).map(request => (
              <div key={request.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{request.projectName}</h4>
                  <p className="text-sm text-gray-600">{request.proposalsCount} proposals received</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                    {getStatusLabel(request.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Status</h3>
          <div className="space-y-4">
            {projects.slice(0, 3).map(project => (
              <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{project.name}</h4>
                  <p className="text-sm text-gray-600">Credibility Score: {project.credibilityScore}/100</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(project.verificationStatus)}`}>
                    {getStatusLabel(project.verificationStatus)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderRequestCard = (request) => (
    <div key={request.id} className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{request.projectName}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
              {getStatusLabel(request.status)}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
            <div className="flex items-center space-x-1">
              <MapPin className="h-4 w-4" />
              <span>{request.location}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="h-4 w-4" />
              <span>Due: {new Date(request.deadline).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Award className="h-4 w-4" />
              <span>{request.preferredStandards.join(', ')}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-green-600">
            ${request.budgetRange.split('-')[0]}k - ${request.budgetRange.split('-')[1]}k
          </div>
          <div className="text-sm text-gray-500">
            {request.proposalsCount} proposals
          </div>
        </div>
      </div>

      <p className="text-gray-700 mb-4">{request.description}</p>

      {request.status === 'in-progress' && request.progress !== undefined && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Verification Progress</span>
            <span className="text-sm font-medium text-gray-900">{request.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${request.progress}%` }}
            ></div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          Created: {new Date(request.requestDate).toLocaleDateString()}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSelectedRequest(request)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="h-4 w-4 inline mr-1" />
            View Details
          </button>
          {request.status === 'proposals-received' && (
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              Review Proposals ({request.proposalsCount})
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderProposalCard = (proposal) => (
    <div key={proposal.id} className="bg-white rounded-lg border border-gray-200 p-6 mb-4">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h4 className="text-lg font-semibold text-gray-900">{proposal.verifierName}</h4>
            <div className="flex items-center space-x-1">
              <Star className="h-4 w-4 text-yellow-400 fill-current" />
              <span className="text-sm text-gray-600">{proposal.verifierRating}</span>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
            <span>{proposal.verifierExperience} experience</span>
            <span>{proposal.completedProjects} projects completed</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xl font-semibold text-green-600">${proposal.price.toLocaleString()}</div>
          <div className="text-sm text-gray-500">{proposal.timeline}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <h5 className="font-medium text-gray-900 mb-2">Methodology</h5>
          <p className="text-sm text-gray-700">{proposal.methodology}</p>
        </div>
        <div>
          <h5 className="font-medium text-gray-900 mb-2">Scope</h5>
          <p className="text-sm text-gray-700">{proposal.scope}</p>
        </div>
      </div>

      <div className="mb-4">
        <h5 className="font-medium text-gray-900 mb-2">Credentials</h5>
        <div className="flex flex-wrap gap-2">
          {proposal.credentials.map(credential => (
            <span key={credential} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {credential}
            </span>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <h5 className="font-medium text-gray-900 mb-2">Recent Portfolio</h5>
        <div className="space-y-1">
          {proposal.portfolio.map((project, index) => (
            <div key={index} className="text-sm text-gray-600">
              {project.name} - {project.credits.toLocaleString()} tCO2e ({project.year})
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          Submitted: {new Date(proposal.submittedDate).toLocaleDateString()}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowProposalDetails(proposal)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            View Details
          </button>
          <button className="px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg hover:bg-blue-50">
            <MessageSquare className="h-4 w-4 inline mr-1" />
            Message
          </button>
          <button
            onClick={() => handleAcceptProposal(selectedRequest.id, proposal.id)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Accept Proposal
          </button>
        </div>
      </div>
    </div>
  );

  const renderRequestDetails = () => {
    if (!selectedRequest) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-6xl w-full max-h-screen overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Verification Request: {selectedRequest.projectName}
              </h2>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {selectedRequest.status === 'in-progress' ? (
              // Show progress view for in-progress requests
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-medium text-blue-900 mb-2">Verification Progress</h3>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-blue-700">Overall Progress</span>
                    <span className="text-sm font-medium text-blue-900">{selectedRequest.progress}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-3">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${selectedRequest.progress}%` }}
                    ></div>
                  </div>
                </div>

                {selectedRequest.milestones && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">Project Milestones</h3>
                    <div className="space-y-4">
                      {selectedRequest.milestones.map(milestone => (
                        <div key={milestone.id} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                          <div className={`p-2 rounded-full ${getStatusColor(milestone.status)}`}>
                            {milestone.status === 'completed' ? (
                              <CheckCircle className="h-4 w-4" />
                            ) : milestone.status === 'in-progress' ? (
                              <Clock className="h-4 w-4" />
                            ) : (
                              <Calendar className="h-4 w-4" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-medium text-gray-900">{milestone.name}</h4>
                              <span className="text-sm text-gray-500">{new Date(milestone.date).toLocaleDateString()}</span>
                            </div>
                            <p className="text-sm text-gray-700">{milestone.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedRequest.communications && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">Recent Communications</h3>
                    <div className="space-y-3">
                      {selectedRequest.communications.map((comm, index) => (
                        <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                          <MessageSquare className="h-4 w-4 text-gray-500 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-gray-900">{comm.from}</span>
                              <span className="text-xs text-gray-500">{new Date(comm.date).toLocaleDateString()}</span>
                              <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusColor(comm.type)}`}>
                                {comm.type}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{comm.content}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Show proposals view for requests with proposals
              <div className="space-y-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3">Request Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Budget Range:</span>
                      <p className="text-gray-900">${selectedRequest.budgetRange.replace('-', ' - ')}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Deadline:</span>
                      <p className="text-gray-900">{new Date(selectedRequest.deadline).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Standards:</span>
                      <p className="text-gray-900">{selectedRequest.preferredStandards.join(', ')}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Credits:</span>
                      <p className="text-gray-900">{selectedRequest.estimatedCredits.toLocaleString()} tCO2e</p>
                    </div>
                  </div>
                </div>

                {selectedRequest.proposals && selectedRequest.proposals.length > 0 && (
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">
                      Received Proposals ({selectedRequest.proposals.length})
                    </h3>
                    {selectedRequest.proposals.map(proposal => renderProposalCard(proposal))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'requests', label: 'Verification Requests', icon: FileText },
    { id: 'progress', label: 'In Progress', icon: Clock },
    { id: 'completed', label: 'Completed', icon: CheckCircle }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'requests':
        return (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="proposals-received">Proposals Received</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Plus className="h-4 w-4 inline mr-1" />
                New Request
              </button>
            </div>

            {/* Request Cards */}
            <div className="space-y-6">
              {verificationRequests
                .filter(request => {
                  const matchesSearch = request.projectName.toLowerCase().includes(searchTerm.toLowerCase());
                  const matchesFilter = filterStatus === 'all' || request.status === filterStatus;
                  return matchesSearch && matchesFilter;
                })
                .map(request => renderRequestCard(request))}
            </div>
          </div>
        );
      case 'progress':
        return (
          <div className="space-y-6">
            {verificationRequests
              .filter(request => request.status === 'in-progress')
              .map(request => renderRequestCard(request))}
            {verificationRequests.filter(r => r.status === 'in-progress').length === 0 && (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No verifications in progress</h3>
                <p className="text-gray-600">Accept a proposal to start the verification process</p>
              </div>
            )}
          </div>
        );
      case 'completed':
        return (
          <div className="text-center py-12">
            <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No completed verifications</h3>
            <p className="text-gray-600">Completed verifications will appear here</p>
          </div>
        );
      default:
        return renderOverview();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Verification Dashboard</h1>
          <p className="text-gray-600 mt-2">
            Manage your verification requests and track progress
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-1 mb-8 border-b border-gray-200">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        {renderTabContent()}

        {/* Request Details Modal */}
        {renderRequestDetails()}
      </div>
    </div>
  );
};

export default ProjectVerificationDashboard; 